import React from 'react';
import { groupBy, includes, move, remove, slice, update } from 'ramda';

import styled from 'styled-components';

import ColumnName from '@/util/ColumnName';
import { TChartType } from '@/features/chart-configuration/apiSlice';
import {
  CHART_TYPE_TO_DIMENSIONS_RULE,
  REQUIRED_COMBINATIONS_RULES,
} from '@/features/chart-configuration/constants';
import {
  getAvailableDimensions,
  isNotConfigurable,
} from '@/features/chart-configuration/utils';
import { Button } from '@trevor/ui-components';
import { Configuration, type ConfigurationProps } from './Configuration';

const Container = styled.div`
  display: flex;
  margin-top: var(--spacing-m);

  > span {
    width: 90px;
    padding-top: 13px;
    font-size: 14px;
  }
`;

const ButtonContainer = styled.div`
  margin-left: auto;
  margin-right: 45px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

export const DimensionsConfiguration: React.FC<{
  chartType: TChartType;
  widgetChartType?: Nullable<string>;
  onChange: F<ColumnName[]>;
  widgetDimensions?: ColumnName[];
  metrics: Nullable<ColumnName[]>;
  dimensionColumnsCombinations: Nullable<ColumnName[][]>;
  prefill: Maybe<ColumnName[]>;
  onUserInteraction?: F0;
}> = ({
  chartType,
  onChange,
  widgetDimensions,
  widgetChartType,
  metrics,
  dimensionColumnsCombinations,
  prefill,
  onUserInteraction,
}) => {
  const [optionsCombinations, setOptionsCombinations] = React.useState<
    ColumnName[][]
  >([]);
  const [selectedColumns, setSelectedColumns] = React.useState<ColumnName[]>(
    [],
  );

  const [minSize, setMinSize] = React.useState(0);
  const [maxSize, setMaxSize] = React.useState(0);
  const [currentSize, setCurrentSize] = React.useState(0);
  const [interacted, setInteracted] = React.useState(false);
  const [groupedBySizeOptions, setGroupedBySizeOptions] = React.useState<
    Record<string, ColumnName[][]>
  >({});

  const configurationRule = CHART_TYPE_TO_DIMENSIONS_RULE[chartType];

  React.useEffect(() => {
    if (dimensionColumnsCombinations?.length) {
      setInteracted(true);
      const sizes = dimensionColumnsCombinations.map((c) => c.length);
      const min = Math.min(...sizes);
      setMinSize(configurationRule.min === 0 ? 0 : min);
      setMaxSize(Math.max(...sizes));
      const groupedOptions = groupBy(
        (c) => c.length.toString(),
        dimensionColumnsCombinations,
      );
      setGroupedBySizeOptions(groupedOptions);

      const dimensionCount =
        !interacted && widgetDimensions?.length && widgetChartType === chartType
          ? widgetDimensions?.length
          : min;

      const firstCombination = groupedOptions[dimensionCount];
      const preselected =
        !interacted && widgetDimensions?.length && widgetChartType === chartType
          ? widgetDimensions
          : prefill || firstCombination[0];

      if (preselected) {
        setSelectedColumns(preselected);
        recalculateOptions(preselected, groupedOptions);
        setCurrentSize(preselected.length);
      }
    } else {
      setCurrentSize(0);
      setSelectedColumns([]);
      setOptionsCombinations([]);
      setMinSize(0);
      setMaxSize(0);
    }
  }, [dimensionColumnsCombinations]);

  const recalculateOptions = (
    columns: ColumnName[],
    groupedOptions: Record<string, ColumnName[][]>,
  ) => {
    const combinations: ColumnName[][] = [];
    columns.forEach((_, index) => {
      combinations.push(
        getAvailableDimensions(
          groupedOptions[columns.length],
          index === 0 ? [] : slice(0, index, columns),
        ),
      );
    });
    setOptionsCombinations(combinations);
    return combinations;
  };

  React.useEffect(() => {
    if (selectedColumns) {
      onChange(selectedColumns);
    }
  }, [selectedColumns]);

  React.useEffect(() => {
    const combination = REQUIRED_COMBINATIONS_RULES[chartType];
    if (combination && metrics) {
      const rules = combination.rules.filter(
        (c) =>
          c.metrics.min <= metrics.length && metrics.length <= c.metrics.max,
      );
      if (rules) {
        if (rules.length === 1) {
          setMaxSize(Math.min(rules[0].dimensions.max, maxSize));
        } else if (dimensionColumnsCombinations) {
          const sizes = dimensionColumnsCombinations.map((c) => c.length);
          setMaxSize(Math.max(...sizes));
        }
      }
    }
  }, [metrics?.length, chartType]);

  if (
    isNotConfigurable(configurationRule) ||
    !dimensionColumnsCombinations ||
    selectedColumns.length === 0
  ) {
    return null;
  }

  const validateSelectedOptions = (
    combinations: ColumnName[][],
    selected: ColumnName[],
  ) => {
    let newColumns = selected;
    if (combinations && combinations.length === selected.length) {
      selected.forEach((column, index) => {
        const optionsCombination = combinations[index];
        if (
          !includes(column, optionsCombination) &&
          optionsCombination.length > 0
        ) {
          newColumns = update(index, optionsCombination[0], newColumns);
        }
      });
    }
    return newColumns;
  };

  const handleOptionSelect: ConfigurationProps<ColumnName>['onOptionSelect'] = (
    index,
    option,
  ) => {
    let newColumns = update(index, option, selectedColumns);
    const newCombinations = recalculateOptions(
      newColumns,
      groupedBySizeOptions,
    );
    newColumns = validateSelectedOptions(newCombinations, newColumns);
    setSelectedColumns(newColumns);
    onUserInteraction?.();
  };

  const handleReorderSelects: ConfigurationProps<ColumnName>['onReorderSelects'] =
    (sourceIndex, destinationIndex) => {
      const newColumns = move(sourceIndex, destinationIndex, selectedColumns);
      recalculateOptions(newColumns, groupedBySizeOptions);
      setSelectedColumns(newColumns);
      onUserInteraction?.();
    };

  const handleSelectRemove: ConfigurationProps<ColumnName>['onSelectRemove'] = (
    index,
  ) => {
    let selected = remove(index, 1, selectedColumns);
    const newCombinations = recalculateOptions(selected, groupedBySizeOptions);
    selected = validateSelectedOptions(newCombinations, selected);
    setSelectedColumns(selected);
    setCurrentSize(currentSize - 1);
    onUserInteraction?.();
  };

  const handleAddItem = () => {
    const newSize = currentSize + 1;
    setCurrentSize(newSize);
    const newColumns = groupedBySizeOptions[newSize][0];
    if (newColumns) {
      setSelectedColumns(newColumns);
      recalculateOptions(newColumns, groupedBySizeOptions);
      onUserInteraction?.();
    }
  };

  return (
    <>
      <Container>
        <span>Dimensions</span>

        <Configuration
          configurableType="dimensions"
          selectedOptions={selectedColumns}
          optionsCombinations={optionsCombinations}
          onOptionSelect={handleOptionSelect}
          onReorderSelects={handleReorderSelects}
          onSelectRemove={handleSelectRemove}
          min={minSize}
        />
      </Container>
      {currentSize < maxSize && (
        <ButtonContainer>
          <span>select up to {maxSize} items</span>
          <Button variant="secondary" onClick={handleAddItem}>
            add another item
          </Button>
        </ButtonContainer>
      )}
    </>
  );
};
